import React from 'react';
import { createRoot } from 'react-dom/client';
import { Box, Typography, alpha } from '@mui/material';
import { colors, status } from '../../theme';
import {
  CheckCircle,
  FilterList,
  ShowChart,
  Speed,
  Visibility,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ReferenceLine,
  Line,
} from 'recharts';
import html2canvas from 'html2canvas';

export const fmt = (n) => n ? n.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '0';
export const fmtScore = (n) => n != null ? n.toFixed(1) : '—';
export const fmtBn = (n) => n != null ? `${(n / 1e9).toFixed(1)}B` : '—';

export const thSx = {
  fontSize: '0.72rem', fontWeight: 700, color: colors.gray[400],
  letterSpacing: '0.02em', py: 0.8,
  borderBottom: `1px solid ${colors.gray[200]}`, bgcolor: colors.gray[50],
};
export const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: `1px solid ${colors.gray[100]}` };

const DISPLAY_TONES = {
  success: { color: colors.success.main, bg: alpha(colors.success.main, 0.1) },
  info: { color: colors.info.main, bg: alpha(colors.info.main, 0.1) },
  warning: { color: colors.warning.main, bg: alpha(colors.warning.main, 0.1) },
  danger: { color: colors.error.main, bg: alpha(colors.error.main, 0.1) },
  accent: { color: status.purple.main, bg: alpha(status.purple.main, 0.1) },
  muted: { color: colors.gray[500], bg: colors.gray[100] },
};

export const getDisplayTone = (tone) => DISPLAY_TONES[tone] || DISPLAY_TONES.muted;

export const getDisplayText = (item, field, t, fallback = '—') => {
  if (!item) return fallback;
  const text = item[`${field}_text`];
  if (text != null) return text;
  const key = item[`${field}_key`];
  if (key) {
    const translated = t(key, item[`${field}_params`] || {});
    if (translated !== key) return translated;
  }
  return fallback;
};

export const scoreColor = (s) => {
  if (s == null) return colors.gray[400];
  if (s >= 75) return colors.success.main;
  if (s >= 50) return colors.warning.main;
  if (s >= 25) return colors.priceAction.main;
  return colors.error.main;
};

export const ACTION_FILTERS = [
  { key: 'all', tKey: 'vpa.filters.all', color: colors.gray[500], icon: FilterList },
  { key: 'READY', tKey: 'vpa.filters.ready', color: colors.success.main, icon: CheckCircle },
  { key: 'WATCH_CLOSE', tKey: 'vpa.filters.watchClose', color: colors.info.main, icon: Visibility },
  { key: 'RADAR_ONLY', tKey: 'vpa.filters.radar', color: colors.warning.main, icon: Speed },
  { key: 'AVOID', tKey: 'vpa.filters.avoid', color: colors.error.main, icon: WarningIcon },
  { key: 'IGNORE', tKey: 'vpa.filters.ignore', color: colors.gray[400], icon: FilterList },
];

export const getSectorLabel = (row, lang) => {
  if (!row) return '—';
  return row.sector_name?.[lang] || row.sector_name?.en || row.sector_code || '—';
};

const waitForRenderFrames = (count = 2) => new Promise((resolve) => {
  const tick = (remaining) => {
    if (remaining <= 0) {
      resolve();
      return;
    }
    window.requestAnimationFrame(() => tick(remaining - 1));
  };
  tick(count);
});

export const extractVisibleChartTooltip = (chartNode) => {
  if (!(chartNode instanceof HTMLElement)) return null;

  const tooltipWrapper = chartNode.querySelector('.recharts-tooltip-wrapper');
  if (!(tooltipWrapper instanceof HTMLElement)) return null;

  const tooltipStyle = window.getComputedStyle(tooltipWrapper);
  if (tooltipStyle.visibility === 'hidden' || tooltipStyle.display === 'none' || tooltipWrapper.getBoundingClientRect().height <= 0) {
    return null;
  }

  const title = tooltipWrapper.querySelector('.recharts-tooltip-label')?.textContent?.trim() || '';
  const items = Array.from(tooltipWrapper.querySelectorAll('.recharts-tooltip-item')).map((item) => {
    const element = item;
    const style = window.getComputedStyle(element);
    const name = element.querySelector('.recharts-tooltip-item-name')?.textContent?.trim() || '';
    const value = element.querySelector('.recharts-tooltip-item-value')?.textContent?.trim() || '';
    return name && value ? { name, value, color: style.color || colors.gray[600] } : null;
  }).filter(Boolean);

  if (!title && items.length === 0) return null;
  return { title, items };
};

export const ScoreHistoryCard = ({ title, chartData, labels, exportMode = false, exportWidth = 440, exportTooltip = null }) => {
  const chartHeight = exportMode ? 220 : 200;

  const chartBody = exportMode ? (
    <Box data-export-chart-body="true" sx={{ width: exportWidth, maxWidth: '100%', overflow: 'hidden' }}>
      <AreaChart width={exportWidth} height={chartHeight} data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="gradVpa" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.success.main} stopOpacity={0.15} />
            <stop offset="100%" stopColor={colors.success.main} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.gray[100]} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: colors.gray[400] }} />
        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: colors.gray[400] }} width={30} />
        <ReferenceLine y={70} stroke={colors.success.main} strokeDasharray="3 3" strokeOpacity={0.5} />
        <ReferenceLine y={50} stroke={colors.warning.main} strokeDasharray="3 3" strokeOpacity={0.5} />
        <Area type="monotone" dataKey="score" stroke={colors.success.main} fill="url(#gradVpa)" strokeWidth={2} dot={false} isAnimationActive={false} />
        <Line type="monotone" dataKey="setup" stroke={colors.info.main} strokeWidth={1.5} dot={false} strokeDasharray="4 2" isAnimationActive={false} />
        <Line type="monotone" dataKey="penalty" stroke={colors.error.main} strokeWidth={1} dot={false} strokeDasharray="2 2" isAnimationActive={false} />
      </AreaChart>
    </Box>
  ) : (
    <Box data-export-chart-body="true">
      <ResponsiveContainer width="100%" height={chartHeight}>
        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gradVpa" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.success.main} stopOpacity={0.15} />
              <stop offset="100%" stopColor={colors.success.main} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.gray[100]} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: colors.gray[400] }} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: colors.gray[400] }} width={30} />
          <RTooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: `1px solid ${colors.gray[200]}` }}
            formatter={(v, name) => [fmtScore(v), name === 'score' ? labels.score : name === 'setup' ? labels.setup : labels.penalty]}
          />
          <ReferenceLine y={70} stroke={colors.success.main} strokeDasharray="3 3" strokeOpacity={0.5} />
          <ReferenceLine y={50} stroke={colors.warning.main} strokeDasharray="3 3" strokeOpacity={0.5} />
          <Area type="monotone" dataKey="score" stroke={colors.success.main} fill="url(#gradVpa)" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="setup" stroke={colors.info.main} strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
          <Line type="monotone" dataKey="penalty" stroke={colors.error.main} strokeWidth={1} dot={false} strokeDasharray="2 2" />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );

  return (
    <Box data-export-anchor="true" data-export-chart="true" sx={{ p: 2, pb: exportMode ? 3 : 2, borderRadius: 2, bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}` }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.gray[800], mb: 1 }}>
        {title}
      </Typography>
      {exportMode && exportTooltip ? (
        <Box sx={{ mb: 1.1, p: 1.2, borderRadius: 2, border: `1px solid ${colors.gray[200]}`, bgcolor: colors.background.paper, boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)' }}>
          {exportTooltip.title ? (
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: colors.gray[900], mb: 0.8 }}>
              {exportTooltip.title}
            </Typography>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.45 }}>
            {exportTooltip.items.map((item) => (
              <Typography key={`${item.name}-${item.value}`} sx={{ fontSize: '0.66rem', fontWeight: 700, color: item.color }}>
                {item.name} : {item.value}
              </Typography>
            ))}
          </Box>
        </Box>
      ) : null}
      {chartBody}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.2, justifyContent: 'center', flexWrap: 'wrap' }}>
        {[
          { label: labels.score, color: colors.success.main },
          { label: labels.setup, color: colors.info.main },
          { label: labels.penalty, color: colors.error.main },
        ].map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 3, borderRadius: 1, bgcolor: item.color }} />
            <Typography sx={{ fontSize: '0.62rem', color: colors.gray[400] }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const renderExportChartImage = async ({ chartData, title, labels, width, exportTooltip = null }) => {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.style.width = `${width}px`;
  host.style.overflow = 'visible';
  host.style.background = colors.gray[50];
  host.style.pointerEvents = 'none';
  host.style.zIndex = '-1';
  document.body.appendChild(host);

  const root = createRoot(host);

  try {
    root.render(
      <Box sx={{ width, p: 0, m: 0, bgcolor: colors.gray[50] }}>
        <ScoreHistoryCard
          title={title}
          chartData={chartData}
          labels={labels}
          exportMode
          exportWidth={Math.max(320, width - 32)}
          exportTooltip={exportTooltip}
        />
      </Box>
    );

    await waitForRenderFrames(4);

    const target = host.querySelector('[data-export-chart="true"]') || host.firstElementChild;
    if (!(target instanceof HTMLElement)) return null;
    const rect = target.getBoundingClientRect();
    const exportWidth = Math.ceil(Math.max(rect.width, target.scrollWidth));
    const exportHeight = Math.ceil(Math.max(rect.height, target.scrollHeight)) + 8;
    const canvas = await html2canvas(target, {
      backgroundColor: colors.gray[50],
      scale: 2,
      useCORS: true,
      width: exportWidth,
      height: exportHeight,
      windowWidth: window.innerWidth,
      windowHeight: Math.max(exportHeight + 64, window.innerHeight),
      scrollX: 0,
      scrollY: 0,
    });

    return {
      src: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height,
      cssWidth: exportWidth,
      cssHeight: exportHeight,
    };
  } finally {
    root.unmount();
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
  }
};

export const exportDrawerCapture = async ({ captureNode, scrollNode, fileName, backgroundColor = colors.gray[50], chartReplacement = null }) => {
  if (!captureNode || !scrollNode) return;

  const captureRect = captureNode.getBoundingClientRect();
  const clone = captureNode.cloneNode(true);

  try {
    {
      const liveEls = captureNode.querySelectorAll('*');
      const cloneEls = clone.querySelectorAll('*');
      if (liveEls.length === cloneEls.length) {
        for (let i = 0; i < liveEls.length; i += 1) {
          const live = liveEls[i];
          const cl = cloneEls[i];
          if (!(live instanceof HTMLElement) || !(cl instanceof HTMLElement)) continue;
          const parent = live.parentElement;
          if (!parent) continue;
          const parentStyle = window.getComputedStyle(parent);
          const ownStyle = window.getComputedStyle(live);
          const isFlexWrapChild = parentStyle.display.includes('flex') && parentStyle.flexWrap === 'wrap';
          const isFlexWrapContainer = ownStyle.display.includes('flex') && ownStyle.flexWrap === 'wrap';
          const isChip = live.classList.contains('MuiChip-root');
          if (isFlexWrapChild || isChip || isFlexWrapContainer) {
            const w = live.getBoundingClientRect().width;
            if (w > 0) {
              if (!isFlexWrapContainer) cl.style.flex = '0 0 auto';
              cl.style.width = `${w}px`;
              cl.style.minWidth = `${w}px`;
              if (!isFlexWrapContainer) cl.style.maxWidth = `${w}px`;
            }
          }
        }
      }
    }

    clone.querySelectorAll('[data-html2canvas-ignore="true"]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.dataset.exportHeaderToolbar === 'true') {
        node.removeAttribute('data-html2canvas-ignore');
        node.style.visibility = 'hidden';
        node.style.pointerEvents = 'none';
      } else {
        node.remove();
      }
    });

    clone.querySelectorAll('[data-export-chart="true"]').forEach((node) => node.remove());

    clone.style.position = 'fixed';
    clone.style.left = '-10000px';
    clone.style.top = '0';
    clone.style.width = `${Math.ceil(captureRect.width)}px`;
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    clone.style.minHeight = '0';
    clone.style.overflow = 'visible';
    clone.style.pointerEvents = 'none';
    clone.style.zIndex = '-1';
    clone.style.background = backgroundColor;
    clone.querySelectorAll('*').forEach((el) => {
      if (el instanceof HTMLElement) {
        const cs = window.getComputedStyle(el);
        if (cs.minHeight && cs.minHeight !== '0px' && cs.minHeight !== 'auto') {
          el.style.minHeight = '0';
        }
      }
    });

    const cloneScrollRoot = clone.querySelector('[data-export-scroll-root="true"]');
    if (cloneScrollRoot instanceof HTMLElement) {
      cloneScrollRoot.style.overflowY = 'visible';
      cloneScrollRoot.style.height = 'auto';
      cloneScrollRoot.style.maxHeight = 'none';
      cloneScrollRoot.style.flex = '0 0 auto';
      if (chartReplacement) {
        cloneScrollRoot.style.paddingBottom = '0px';
      }
    }

    document.body.appendChild(clone);

    await waitForRenderFrames(2);
    window.dispatchEvent(new Event('resize'));
    await waitForRenderFrames(3);

    const cloneRect = clone.getBoundingClientRect();
    const exportWidth = Math.ceil(cloneRect.width);
    const measureContentHeight = () => {
      const cloneTop = cloneRect.top;
      let maxBottom = cloneRect.top;
      const walk = (el) => {
        if (!(el instanceof HTMLElement)) return;
        const cs = window.getComputedStyle(el);
        if (cs.visibility === 'hidden' || cs.display === 'none') return;
        const r = el.getBoundingClientRect();
        if (r.bottom > maxBottom) maxBottom = r.bottom;
        for (const child of el.children) walk(child);
      };
      for (const child of clone.children) walk(child);
      return Math.max(0, Math.ceil(maxBottom - cloneTop));
    };
    const contentHeight = measureContentHeight();
    const bottomPad = chartReplacement ? 0 : 24;
    const exportHeight = contentHeight > 0 ? contentHeight + bottomPad : Math.ceil(cloneRect.height);

    const drawerCanvas = await html2canvas(clone, {
      backgroundColor,
      scale: 2,
      useCORS: true,
      width: exportWidth,
      height: exportHeight,
      windowWidth: window.innerWidth,
      windowHeight: Math.max(exportHeight, window.innerHeight),
      scrollX: 0,
      scrollY: 0,
    });

    const gap = chartReplacement ? 16 : 0;
    const chartScaledWidth = chartReplacement ? Math.round(chartReplacement.width) : 0;
    const chartScaledHeight = chartReplacement ? Math.round(chartReplacement.height) : 0;
    const finalWidth = drawerCanvas.width;
    const finalHeight = drawerCanvas.height + (chartReplacement ? chartScaledHeight + gap * 2 : 0);

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = finalWidth;
    finalCanvas.height = finalHeight;
    const ctx = finalCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, finalWidth, finalHeight);
      ctx.drawImage(drawerCanvas, 0, 0);

      if (chartReplacement) {
        const chartImg = new Image();
        chartImg.src = chartReplacement.src;
        await new Promise((resolve) => {
          chartImg.onload = () => resolve();
          chartImg.onerror = () => resolve();
        });
        const dx = Math.max(0, Math.floor((finalWidth - chartScaledWidth) / 2));
        const dy = drawerCanvas.height + gap;
        ctx.drawImage(chartImg, dx, dy, chartScaledWidth, chartScaledHeight);
      }
    }

    const link = document.createElement('a');
    link.download = fileName;
    link.href = finalCanvas.toDataURL('image/png');
    link.click();
  } finally {
    if (clone.parentNode) {
      clone.parentNode.removeChild(clone);
    }
  }
};

export const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    borderRadius: 2, border: `1px solid ${colors.gray[200]}`,
    bgcolor: colors.background.paper, p: '8px 14px',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Icon sx={{ fontSize: 14, color }} />
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: colors.gray[600], lineHeight: 1 }}>{label}</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.2, ml: 0.2 }}>
      <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1.15, fontFamily: 'monospace' }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: '0.6rem', fontWeight: 500, color: colors.gray[400], whiteSpace: 'nowrap' }}>{sub}</Typography>}
    </Box>
  </Box>
);

export const ScoreBar = ({ score, maxScore = 100, height = 6 }) => {
  const pct = Math.min(100, Math.max(0, (score / maxScore) * 100));
  const c = scoreColor(score);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
      <Box sx={{ flex: 1, height, borderRadius: 1, bgcolor: alpha(c, 0.1), overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 1, bgcolor: c, transition: 'width 0.3s ease' }} />
      </Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: c, minWidth: 32, textAlign: 'right', fontFamily: 'monospace' }}>
        {fmtScore(score)}
      </Typography>
    </Box>
  );
};

export const EmptyState = ({ Icon, title, subtitle }) => (
  <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: colors.gray[50], border: `1.5px dashed ${colors.gray[200]}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {Icon && <Icon sx={{ fontSize: 24, color: colors.gray[300] }} />}
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: colors.gray[400], lineHeight: 1.4 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.72rem', color: colors.gray[300], mt: 0.3, maxWidth: 400 }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

export const EmptyStateIcon = ShowChart;
